import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Dialog } from '@/ui/Dialog';
import { Field, Input, Textarea } from '@/ui/Field';
import { SkeletonTarjetas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { Icono } from '@/components/Icono';
import { usePlantilla, usePlantillas, useRadicar, type Plantilla } from './api';

/**
 * NuevaSolicitud — elegir un formato y rellenarlo.
 *
 * La rejilla usaba `.platzi-card`, y esta pantalla era su **último consumidor
 * del portal**: la clase se fue con ella. Aquí las tarjetas son `Card` del
 * sistema, que es la misma caja que usa todo lo demás.
 */
export function NuevaSolicitud({ activa }: { activa: boolean }) {
  const consulta = usePlantillas(activa);
  const [elegida, setElegida] = useState<Plantilla | null>(null);
  const plantillas = consulta.data ?? [];

  if (consulta.isPending) return <SkeletonTarjetas n={4} />;
  if (consulta.isError) {
    return (
      <EstadoError
        err={consulta.error}
        que="los formatos de solicitud"
        onReintentar={() => void consulta.refetch()}
      />
    );
  }
  if (!plantillas.length) {
    return (
      <EstadoVacio
        icono="bi-file-earmark-x"
        titulo="Sin formatos disponibles"
        descripcion="Bienestar todavía no ha publicado formatos de solicitud."
      />
    );
  }

  return (
    <>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {plantillas.map((t) => (
          <li key={t.id} className="flex">
            <Card interactiva className="flex w-full flex-col overflow-hidden">
              <button
                type="button"
                className="flex flex-1 flex-col text-left focus-visible:focus-anillo"
                onClick={() => setElegida(t)}
              >
                {/* El color lo elige quien crea la plantilla: es dato, no tema. */}
                <span
                  className="flex h-20 items-center justify-center text-8 text-on-brand"
                  style={{ background: t.color }}
                  aria-hidden="true"
                >
                  <Icono nombre={t.icon} respaldo="bi-file-earmark-text" />
                </span>
                <span className="flex flex-1 flex-col gap-1 p-3">
                  <strong className="text-3 font-bold text-brand-ink">{t.name}</strong>
                  {t.description && (
                    <span className="line-clamp-2 text-2 text-muted">{t.description}</span>
                  )}
                  <span className="mt-auto pt-1 text-2 text-muted">
                    <i className="bi bi-pencil-square" aria-hidden="true" />{' '}
                    {t.manual_fields.length}{' '}
                    {t.manual_fields.length === 1 ? 'dato a llenar' : 'datos a llenar'}
                  </span>
                </span>
              </button>
            </Card>
          </li>
        ))}
      </ul>

      {elegida && (
        <FormularioDeSolicitud
          plantillaId={elegida.id}
          nombre={elegida.name}
          onCerrar={() => setElegida(null)}
        />
      )}
    </>
  );
}

/** El formulario de una plantilla concreta. Se vuelve a pedir la plantilla
 *  porque la lista trae solo los campos manuales resumidos. */
function FormularioDeSolicitud({
  plantillaId, nombre, onCerrar,
}: {
  plantillaId: number;
  nombre: string;
  onCerrar: () => void;
}) {
  const consulta = usePlantilla(plantillaId);
  const radicar = useRadicar();
  const [valores, setValores] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const campos = consulta.data?.manual_fields ?? [];

  return (
    <Dialog
      abierto
      onCambio={(v) => { if (!v && !radicar.isPending) onCerrar(); }}
      titulo={nombre}
      descripcion="Tus datos —nombre, cédula, cargo, área y fecha— se completan solos."
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button
            loading={radicar.isPending}
            onClick={() => {
              const faltan = campos.filter(
                (f) => f.required && !(valores[f.name] ?? '').trim());
              if (faltan.length) {
                setError(`Falta ${faltan.length === 1 ? 'el campo' : 'los campos'}: ${faltan.map((f) => f.label).join(', ')}.`);
                return;
              }
              radicar.mutate(
                { template_id: plantillaId, data: valores },
                {
                  onSuccess: (r) => {
                    Utils.toast(`Solicitud enviada — radicado ${r.radicado}`, 'success', 5000);
                    onCerrar();
                  },
                  onError: (err) => setError(
                    err instanceof Error ? err.message : 'No se pudo enviar'),
                },
              );
            }}
          >
            <i className="bi bi-send-fill" aria-hidden="true" /> Enviar a Bienestar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 pb-2">
        {consulta.isError ? (
          <EstadoError
            err={consulta.error}
            que="el formato"
            onReintentar={() => void consulta.refetch()}
          />
        ) : !campos.length ? (
          <p className="text-3 text-muted">
            {consulta.isPending ? 'Cargando el formato…' : 'Este formato no pide datos adicionales.'}
          </p>
        ) : (
          campos.map((f) => (
            <Field
              key={f.name}
              label={f.label}
              opcional={!f.required}
              error={error && f.required && !(valores[f.name] ?? '').trim() ? error : undefined}
            >
              {f.type === 'textarea' ? (
                <Textarea
                  rows={2}
                  value={valores[f.name] ?? ''}
                  onChange={(e) => {
                    setValores((v) => ({ ...v, [f.name]: e.target.value }));
                    setError('');
                  }}
                />
              ) : (
                <Input
                  type={f.type === 'date' ? 'date' : 'text'}
                  value={valores[f.name] ?? ''}
                  onChange={(e) => {
                    setValores((v) => ({ ...v, [f.name]: e.target.value }));
                    setError('');
                  }}
                />
              )}
            </Field>
          ))
        )}

        {error && campos.every((f) => !f.required || (valores[f.name] ?? '').trim()) && (
          <p role="alert" className="text-2 text-danger-ink">{error}</p>
        )}
      </div>
    </Dialog>
  );
}
